import {
  VStack,
  HStack,
  Divider,
  Text,
  Icon,
  Spinner,
  Badge,
} from "@chakra-ui/react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { KYDInternalCampaign } from "@common/interfaces/KYDCampaign";
import StatCard from "../../Components/StatCard";
import {
  FaEnvelope,
  FaPlay,
  FaPause,
  FaTicketAlt,
  FaHourglass,
} from "react-icons/fa";
import dayjs from "dayjs";
import { CalendarIcon, CheckIcon } from "@chakra-ui/icons";
import { IoSend } from "react-icons/io5";
import { formatDate } from "src/utils";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";

interface HeadlineProps {
  title: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
}

const Headline = ({ title, icon, children }: HeadlineProps) => {
  return (
    <HStack gap={2} alignItems="center" justifyContent="flex-start" mb={2}>
      {icon}
      <Text
        fontSize="24px"
        fontWeight="600"
        textAlign="left"
        color="#1A202C"
        letterSpacing="-0.72px"
      >
        {title}
      </Text>
      {children}
    </HStack>
  );
};

interface SubheadlineProps {
  title: string;
}

const Subheadline = ({ title }: SubheadlineProps) => {
  return (
    <Text
      fontSize="16px"
      fontWeight="500"
      textAlign="left"
      color="rgba(26, 32, 44, 0.75)"
      letterSpacing="-0.48px"
      lineHeight="150%"
    >
      {title}
    </Text>
  );
};

const colorForStatus = (
  status: KYDInternalCampaign["status"],
  background: boolean = false
) => {
  if (status === "ACTIVE") return !background ? "yellow.400" : "yellow.100";
  if (status === "OPEN") return !background ? "blue.400" : "blue.100";
  if (status === "PAUSED") return !background ? "gray.400" : "gray.100";
  if (status === "COMPLETED") return !background ? "green.400" : "green.100";
  return !background ? "gray.400" : "gray.100";
};

const IconForStatus = ({
  status,
  color,
}: {
  status: KYDInternalCampaign["status"];
  color: string;
}) => {
  if (status === "ACTIVE") return <FaHourglass color={color} />;
  if (status === "OPEN") return <CalendarIcon color={color} />;
  if (status === "PAUSED") return <FaPause color={color} />;
  if (status === "COMPLETED") return <IoSend color={color} />;
  return <CalendarIcon color={color} />;
};

const TextForStatus = ({
  status,
}: {
  status: KYDInternalCampaign["status"];
}) => {
  const TextComp = ({ children }: { children: React.ReactNode }) => (
    <Text fontSize="12px" textTransform="uppercase">
      {children}
    </Text>
  );

  if (status === "ACTIVE") return <TextComp>Active</TextComp>;
  if (status === "OPEN") return <TextComp>Scheduled</TextComp>;
  if (status === "PAUSED") return <TextComp>Paused</TextComp>;
  if (status === "COMPLETED") return <TextComp>Sent</TextComp>;

  return <TextComp>Draft</TextComp>;
};

const CampaignStatusBadge = ({
  status,
}: {
  status: KYDInternalCampaign["status"];
}) => {
  return (
    <Badge
      background={colorForStatus(status, true)}
      color={colorForStatus(status)}
    >
      <HStack gap={2} alignItems="center" justifyContent="center">
        <IconForStatus status={status} color={colorForStatus(status)} />
        <TextForStatus status={status} />
      </HStack>
    </Badge>
  );
};

const Metrics = ({
  isMobile,
  campaign,
  onCancelCampaign,
  permissions,
}: {
  isMobile: boolean;
  permissions: KYDScope[];
  campaign: KYDInternalCampaign;
  onCancelCampaign: () => void;
}) => {
  const sentInFuture = dayjs(campaign.send_at || new Date()).isAfter(dayjs());
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <VStack
        w={isMobile ? "100%" : "443px"}
        bg="white"
        h="100%"
        alignItems="flex-start"
        position="relative"
      >
        <Divider width={"calc(100% + 80px)"} ml={-8} borderColor="#00000033" />
        <HStack gap={2} py={2} alignItems="center" w="full">
          <Text
            color="#888888"
            fontWeight="500"
            fontSize="14px"
            letterSpacing="-0.42px"
            lineHeight="140%"
          >
            <CampaignStatusBadge status={campaign.status} />
          </Text>
          <HStack gap={1}>
            <Text textAlign="left" color="#" lineHeight="24px" fontSize="small">
              {sentInFuture ? "For" : "On"}
            </Text>
            <Text as="span" fontWeight="bold" fontSize="small">
              {formatDate(dayjs(campaign.send_at || new Date()))}
            </Text>
          </HStack>
          <ShowIfHasScope
            permissions={permissions}
            checkFor={["crm/campaigns:write", "crm/write"]}
          >
            {sentInFuture && (
              <HStack
                ml={"auto"}
                px={2}
                color="#1D74FF"
                as="button"
                spacing={1}
                onClick={() => onCancelCampaign()}
                cursor="pointer"
              >
                <Text fontSize="small" color="#1D74FF">
                  Edit
                </Text>
              </HStack>
            )}
          </ShowIfHasScope>
        </HStack>

        <Divider width={"calc(100% + 80px)"} ml={-8} borderColor="#00000033" />
        <VStack gap={2} py={2} alignItems="flex-start">
          <Text
            color="#888888"
            fontWeight="500"
            fontSize="14px"
            letterSpacing="-0.42px"
            lineHeight="140%"
          >
            Segment Delivered To
          </Text>
          <Text
            fontSize="20px"
            fontWeight="500"
            textAlign="left"
            color="#222"
            lineHeight="24px"
            width={"100%"}
          >
            {campaign.segments[0]?.name}
          </Text>
        </VStack>
        <Divider width={"calc(100% + 80px)"} ml={-8} borderColor="#00000033" />
        <VStack w="100%" alignItems="flex-start" gap={2} py={2}>
          <Headline
            title="KYD Ticket Sales"
            icon={<Icon as={FaTicketAlt} color="#A7A7A7" />}
          >
            {campaign.status === "ACTIVE" ? <Spinner size="sm" /> : null}
          </Headline>
          <HStack w="100%" spacing={4}>
            <StatCard
              title="Tickets"
              value={`${campaign.display.tickets}`}
              flex="1"
            />
            <StatCard
              title="Average Order Value"
              value={`${campaign.display.average_order_value}`}
              flex="2"
            />
          </HStack>
          <StatCard
            w={"100%"}
            highlight
            isKYD
            title="Revenue"
            value={`${campaign.display.revenue}`}
            flex="1"
          />
        </VStack>
        <Divider width={"calc(100% + 80px)"} ml={-8} borderColor="#00000033" />

        <VStack w="100%" alignItems="flex-start" gap={2}>
          <Headline
            title="Email Metrics"
            icon={<Icon as={FaEnvelope} color="#A7A7A7" />}
          />
          <Subheadline title="Delivery" />
          <HStack w="100%" spacing={4} mb={1}>
            <StatCard
              title="Delivered"
              value={`${campaign.display.deliveries}`}
              flex="1"
            />
            <StatCard
              title="Opens"
              value={`${campaign.display.unique_opens}`}
              flex="1"
            />
            <StatCard
              title="Open Rate"
              value={`${campaign.display.open_rate}`}
              flex="1"
            />
          </HStack>
          <Subheadline title="Engagement" />
          <HStack w="100%" spacing={2} mb={1} wrap="wrap">
            <HStack w="100%" spacing={2}>
              <StatCard
                title="Clicks"
                value={`${campaign.display.unique_clicks}`}
                flex="1"
              />
              <StatCard
                title="Click Through Rate"
                value={`${campaign.display.click_through_rate}`}
                flex="1"
              />
            </HStack>
            <HStack w="100%" spacing={2}>
              <StatCard
                title="Unsubscribes"
                value={`${campaign.display.unsubscribes}`}
                flex="1"
              />
              {/* <StatCard
                title="Unsubscribed Rate"
                value={`${campaign.analytics.open_rate}%`}
                flex="1"
              /> */}
            </HStack>
          </HStack>
          <Subheadline title="Negative" />
          <HStack w="100%" spacing={2} mb={1}>
            <StatCard
              title="Bounces"
              value={`${campaign.analytics.bounces}`}
              flex="1"
            />
            <StatCard
              title="Complaints"
              value={`${campaign.analytics.complaints}`}
              flex="1"
            />
          </HStack>
        </VStack>
      </VStack>
    </LocalizationProvider>
  );
};

export default Metrics;
